import classNames from "classnames";
import styles from "./AscBullpenEventApplicationStudentListTable.module.scss";
import {
  AdminGetTrackEventApplicationByIdResponse,
  AdminGetTrackEventApplicationsResponse,
  TrackEventApplicationTableItem,
  RequestPagination,
} from "@ascendnow/shared";
import { useEffect, useMemo, useState } from "react";
import AscScrollableTable, {
  AscScrollableTableProps,
} from "../AscScrollableTable/AscScrollableTable";
import AscButton from "../AscButton/AscButton";
import AscTag from "../AscTag/AscTag";
import { AdminMedal1, AdminMedal2, AdminMedal3 } from "../AscIcons";
import dayjs from "dayjs";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";
import AscSearchInput from "../AscInputFields/AscSearchInput/AscSearchInput";
import AscBullpenEventCheckApplicationModal from "../AscBullpenEventCheckApplicationModal/AscBullpenEventCheckApplicationModal";
import { useRouter } from "next/router";

export interface AscBullpenEventApplicationStudentListTableProps {
  className?: string;
  bullpenEventOptions: Record<"id" | "name", string>[];
  onGetEventApplications: (
    bullpenEventId?: string,
    filters?: {
      sort?: { field: string; direction: "asc" | "desc" };
      page?: number;
      searchTerm?: string;
    }
  ) => Promise<{
    eventApplications: AdminGetTrackEventApplicationsResponse;
    pagination: RequestPagination;
  }>;
  onGetEventApplicationById: (
    applicationId: string
  ) => Promise<AdminGetTrackEventApplicationByIdResponse>;
  onSetScore: (applicationId: string, score: number) => Promise<void>;
}

const columns: AscScrollableTableProps<
  keyof TrackEventApplicationTableItem
>["columns"] = [
  {
    name: "Name",
    selector: "name",
    sortable: true,
  },
  {
    name: "Age",
    selector: "age",
    sortable: true,
  },
  {
    name: "Email",
    selector: "email",
    sortable: true,
  },
  {
    name: "Place",
    selector: "place",
    sortable: true,
  },
  {
    name: "School",
    selector: "school",
    sortable: true,
  },
  {
    name: "Event",
    selector: "eventName",
    sortable: true,
  },
  {
    name: "Application",
    selector: "applicationId",
  },
  {
    name: "Status",
    selector: "status",
    sortable: true,
  },
  {
    name: "Points",
    selector: "points",
    sortable: true,
  },
];

export default function AscBullpenEventApplicationStudentListTable({
  className,
  bullpenEventOptions,
  onGetEventApplications,
  onGetEventApplicationById,
  onSetScore,
}: AscBullpenEventApplicationStudentListTableProps) {
  const router = useRouter();
  const page = Number(router.query.page) || 1;
  const sortBy = router.query.sortBy as string;
  const sortOrder = router.query.sortOrder as "asc" | "desc";

  const [eventApplications, setEventApplications] = useState<
    TrackEventApplicationTableItem[]
  >([]);
  const [selectedEvent, setSelectedEvent] = useState<
    Record<"id" | "name", string> | undefined
  >(bullpenEventOptions?.[0]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedApplication, setSelectedApplication] =
    useState<AdminGetTrackEventApplicationByIdResponse>();
  const [pageSize, setPageSize] = useState(10);

  const eventApplicationTableData: Record<
    keyof TrackEventApplicationTableItem,
    React.ReactNode
  >[] = useMemo(
    () =>
      eventApplications?.map((app) => ({
        name: app?.name,
        age: app?.age,
        email: app?.email,
        place: (() => {
          switch (app?.place) {
            case 0:
              return "";
            case 1:
              return (
                <div className={classNames(styles.badge, styles.first)}>
                  <AdminMedal1 />
                </div>
              );
            case 2:
              return (
                <div className={classNames(styles.badge, styles.second)}>
                  <AdminMedal2 />
                </div>
              );
            case 3:
              return (
                <div className={classNames(styles.badge, styles.third)}>
                  <AdminMedal3 />
                </div>
              );
            default:
              return app?.place;
          }
        })(),
        school: app?.school,
        eventName: <b>{app?.eventName}</b>,
        applicationId: (
          <AscButton
            onClick={() => getEventApplicationById(app?.applicationId)}
            size="small"
          >
            Check application
          </AscButton>
        ),
        status:
          app?.status === "active" ? (
            <AscTag label={"Active"} type={"green"} />
          ) : (
            <AscTag label={"Inactive"} type={"red"} />
          ),
        points: app?.points,
      })),
    [eventApplications]
  );

  async function getEventApplicationById(applicationId: string) {
    try {
      setLoading(true);

      const eventApplication = await onGetEventApplicationById(applicationId);

      setSelectedApplication(eventApplication);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSetScore(applicationId: string, score: number) {
    await onSetScore(applicationId, score);
    getEventApplications(selectedEvent?.id, {
      sort: { field: sortBy, direction: sortOrder },
      page,
      searchTerm,
    });
  }

  async function getEventApplications(
    bullpenEventId?: string,
    filters?: {
      sort?: { field: string; direction: "asc" | "desc" };
      page?: number;
      searchTerm?: string;
    }
  ) {
    try {
      setLoading(true);

      const { eventApplications, pagination } = await onGetEventApplications(
        bullpenEventId,
        filters
      );

      setPageSize(pagination?.pageSize || 10);
      setEventApplications(
        eventApplications?.map((app) => ({
          name: app?.name,
          age: `${Math.abs(
            dayjs(app?.birthDate).diff(dayjs(), "years")
          )} years`,
          email: app?.email,
          school: app?.school,
          eventName: app?.event?.name,
          applicationId: app?.id,
          status: app?.student?.user?.isVerified ? "active" : "inactive",
          place: app?.results?.[0]?.rank,
          points: app?.results?.[0]?.score,
        }))
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch() {
    getEventApplications(selectedEvent?.id, {
      sort: { field: sortBy, direction: sortOrder },
      page,
      searchTerm,
    });
  }

  useEffect(() => {
    getEventApplications(selectedEvent?.id, {
      sort: { field: sortBy, direction: sortOrder },
      page,
      searchTerm,
    });
  }, [selectedEvent, sortBy, sortOrder, page]);

  return (
    <div
      data-testid="asc-bullpen-event-application-student-list-table"
      className={classNames(styles.ground, className)}
    >
      <div className={styles.topBar}>
        <div className={styles.eventSelectContainer}>
          <label className={styles.eventSelectLabel} htmlFor="eventSelectField">
            Choose Bullpen Competition
          </label>
          <AscSelectInput
            id="eventSelectField"
            className={styles.eventSelectField}
            placeholder="Select event"
            value={selectedEvent?.id}
            onChange={(value) =>
              setSelectedEvent(
                bullpenEventOptions?.find((event) => event?.id === value)
              )
            }
            disabled={!bullpenEventOptions?.length || loading}
          >
            {bullpenEventOptions.map((event) => (
              <AscSelectInput.Option
                key={event.id}
                value={event.id}
                label={event.name}
              />
            ))}
          </AscSelectInput>
        </div>
        <div className={styles.searchField}>
          <AscSearchInput
            value={searchTerm}
            placeholder="Search by name..."
            onChange={(event) => setSearchTerm(event.target.value)}
            className={styles.input}
            disabled={loading}
          />
          <AscButton
            onClick={handleSearch}
            disabled={loading}
            className={styles.button}
          >
            Search
          </AscButton>
        </div>
      </div>

      <AscScrollableTable
        title="Students list"
        loading={loading}
        columns={columns}
        data={eventApplicationTableData}
        pageCount={eventApplications?.length >= pageSize ? page + 1 : 1}
      />
      <AscBullpenEventCheckApplicationModal
        visible={!!selectedApplication}
        onClose={() => setSelectedApplication(undefined)}
        application={
          selectedApplication as AdminGetTrackEventApplicationByIdResponse
        }
        onSetScore={handleSetScore}
      />
    </div>
  );
}
